import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import API from '@/api/client';
import { formatCurrency, formatDateTime } from '@/utils/helpers';
import { toast } from 'sonner';

const AdminReferrals = () => {
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchReferrals();
  }, [activeTab]);

  const fetchReferrals = async () => {
    try {
      setLoading(true);
      const status = activeTab === 'all' ? '' : activeTab;
      const response = await API.get(`/admin/referrals${status ? `?status=${status}` : ''}`);
      setReferrals(response.data.referrals);
    } catch (error) {
      console.error('Error fetching referrals:', error);
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const getReferralTypeLabel = (type) => {
    const labels = {
      'therapist_to_patient': 'Therapist → Patient',
      'therapist_to_therapist': 'Therapist → Therapist',
      'customer_to_patient': 'Customer → Patient',
      'customer_to_corporate': 'Customer → Corporate',
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center gap-4">
          <img
            src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
            alt="Physiomobile Logo"
            className="h-10"
          />
          <h1 className="font-heading text-2xl font-bold text-gray-800">Flywheel HQ</h1>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8">
        <PageHeader
          title="Referrals Management"
          subtitle="Track and manage referral conversions"
          actions={
            <Button onClick={() => window.location.href = '/admin/dashboard'}>
              Back to Dashboard
            </Button>
          }
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="qualified" data-testid="tab-qualified">Qualified</TabsTrigger>
            <TabsTrigger value="paid" data-testid="tab-paid">Paid</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <LoadingSpinner size="lg" text="Loading referrals..." />
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground mb-4">
              {referrals.length} referrals
            </div>
            {referrals.map((referral, index) => (
              <Card
                key={referral.id}
                className="p-6 hover:shadow-md transition-shadow"
                data-testid={`referral-card-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-mono text-lg font-semibold text-gray-800">
                        {referral.referral_code}
                      </h3>
                      <StatusBadge status={referral.status} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Type</p>
                        <p className="font-medium text-sm">
                          {getReferralTypeLabel(referral.referral_type)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Referrer ID</p>
                        <p className="font-mono text-sm">{referral.referrer_id.slice(0, 8)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Payout Amount</p>
                        <p className="font-medium text-sm">
                          {formatCurrency(referral.payout_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Created</p>
                        <p className="text-sm">{formatDateTime(referral.created_at)}</p>
                      </div>
                    </div>
                    {referral.conversion_date && (
                      <div className="mt-2 text-sm text-green-600">
                        Converted: {formatDateTime(referral.conversion_date)}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminReferrals;